import React, { useEffect, useState, useRef } from "react";
import { Player } from "@lottiefiles/react-lottie-player";
import { motion } from "framer-motion";
import { BedDouble, ShowerHead, CarFront } from "lucide-react"; // Install lucide-react if not done
import { Link } from "react-router-dom"; // Make sure this is imported at the top
import Footer from "../components/navigation/footer";
const images = [
  "https://arkbuild-cdn.s3.ap-southeast-2.amazonaws.com/images/22Ursulla/interior.JPG",
  "https://arkbuild-cdn.s3.ap-southeast-2.amazonaws.com/images/1Madonna/interior.JPG",
  "https://arkbuild-cdn.s3.ap-southeast-2.amazonaws.com/images/1Madonna/bathroom.JPG",
  "https://arkbuild-cdn.s3.ap-southeast-2.amazonaws.com/images/22Ursulla/exterior.JPG",
  "https://arkbuild-cdn.s3.ap-southeast-2.amazonaws.com/images/1Madonna/exterior.JPG",
];

const processSteps = [
  {
    id: 1,
    title: "Design",
    desc: "We collaborate to create a vision and plan.",
  },
  {
    id: 2,
    title: "Approval",
    desc: "Handle council approvals & documentation.",
  },
  {
    id: 3,
    title: "Site Prep",
    desc: "Site preparation and detailed scheduling.",
  },
  {
    id: 4,
    title: "Construction",
    desc: "Execution begins with precision and care.",
  },
  { id: 5, title: "Handover", desc: "Final checks and delivery to you." },
];

const projects = [
  {
    title: "1 Madonna Street, Winston Hills",
    status: "Under Construction",
    image:
      "https://arkbuild-cdn.s3.ap-southeast-2.amazonaws.com/images/1Madonna/exterior.JPG",
    beds: 5,
    baths: 3,
    cars: 1,
    link: "1-Madonna-Street-Winston-Hills",
  },
  {
    title: "22 Ursula Street, Winston Hills",
    status: "Under Construction",
    image:
      "https://arkbuild-cdn.s3.ap-southeast-2.amazonaws.com/images/22Ursulla/exterior.JPG",
    beds: 5,
    baths: 3,
    cars: 2,
    link: "22-Ursula-Street-Winston-Hills",
  },
];

function Homepage() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [fadeIn, setFadeIn] = useState(false);
  const [showTimeline, setShowTimeline] = useState(false);
  const timelineRef = useRef(null);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % images.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => setFadeIn(true), 100);
    return () => clearTimeout(timeout);
  }, []);

  // Observer for timeline section
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setShowTimeline(true);
      },
      { threshold: 0.4 }
    );
    if (timelineRef.current) {
      observer.observe(timelineRef.current);
    }
    return () => {
      if (timelineRef.current) observer.unobserve(timelineRef.current);
    };
  }, []);

  return (
    <div
      className={`w-full overflow-y-scroll scrollbar-hide transition-opacity duration-1000 ${
        fadeIn ? "opacity-100" : "opacity-0"
      }`}
    >
      {/* HERO SECTION */}
      <section className="relative min-h-screen w-full overflow-hidden">
        {images.map((img, index) => (
          <img
            key={index}
            src={img}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-[2000ms] ${
              index === currentIndex ? "opacity-100" : "opacity-0"
            }`}
            alt="background"
          />
        ))}

        <div
          className="absolute inset-0 z-10 pointer-events-none"
          style={{
            background:
              "radial-gradient(circle, rgba(0,0,0,0.3) 30%, rgba(0,0,0,0.6) 100%)",
          }}
        />

        <div className="absolute inset-0 z-20 flex flex-col items-center justify-center text-center px-4">
          <h1
            className="text-white leading-none"
            style={{
              fontFamily: "Montserrat",
              fontSize: "clamp(3rem, 10vw, 10rem)",
            }}
          >
            <span className="font-semibold">ARK</span>
            <span className="font-normal">BUILD</span>
          </h1>
          <div
            className="mt-4 text-white tracking-wide text-base sm:text-lg md:text-xl max-w-[80%]"
            style={{ fontFamily: "Montserrat" }}
          >
            {/* Show this span only on medium and up */}
            <span className="hidden sm:inline whitespace-nowrap">
              Residential Construction | Commercial Construction | Project
              Management
            </span>

            {/* Show stacked version only on small screens */}
            <div className="flex flex-col sm:hidden text-sm space-y-1">
              <span>Residential Construction</span>
              <span>Commercial Construction</span>
              <span>Project Management</span>
            </div>
          </div>
        </div>

        <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 z-30">
          <Player
            autoplay
            loop
            src="https://arkbuild-cdn.s3.ap-southeast-2.amazonaws.com/other/downArrowWhite.json"
            style={{ height: "60px", width: "60px" }}
          />
        </div>
      </section>

      {/* CONSTRUCTION PROCESS */}
      <section className="text-[#333333] px-6 md:px-20 py-20 flex flex-col justify-center bg-gradient-to-b from-[#FCFAF5] to-[#F3EDDD]">
        {" "}
        <div className="flex flex-col lg:flex-row items-center justify-between gap-12">
          {/* Text Block */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
            className="w-full lg:w-1/2 text-left"
          >
            <h2 className="text-6xl font-medium font-interTight">
              Our Construction Process
            </h2>
            <p className="mt-4 text-[clamp(1rem,1.3vw,1.25rem)] text-base font-interTight text-[#333333cc] max-w-xl">
              From concept to completion, we deliver a seamless build experience
              tailored to your needs.
            </p>
          </motion.div>

          {/* Image Block */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true }}
            className="w-full lg:w-1/2"
          >
            <img
              src="https://arkbuild-cdn.s3.ap-southeast-2.amazonaws.com/images/22Ursulla/exterior.JPG"
              alt="Construction"
              className="w-full h-auto rounded-lg shadow-md object-cover"
            />
          </motion.div>
        </div>
        {/* TIMELINE */}
        <div
          ref={timelineRef}
          className="relative mt-24 w-full min-h-[120px] px-2"
        >
          {showTimeline && (
            <>
              {/* Horizontal line - only for medium+ screens */}
              <motion.div
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ delay: 1.5, duration: 1.5, ease: "easeInOut" }}
                className="hidden md:block origin-left absolute top-[73px] left-0 right-0 h-[1.5px] bg-[#F25E35] z-0"
              />

              <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-12 w-full">
                {processSteps.map((step, index) => (
                  <motion.div
                    key={step.id}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 + index * 0.2, duration: 0.5 }}
                    className="flex flex-col items-center text-center w-full md:w-[20%] px-2"
                  >
                    <div className="text-[32px] font-medium">{`0${step.id}`}</div>

                    {/* Dot */}
                    <div className="flex items-center justify-center my-3 h-[28px]">
                      <div className="w-[14px] h-[14px] border-[3px] border-[#F25E35] rounded-full bg-[#F3EDDD]" />
                    </div>

                    <div className="text-base font-semibold">{step.title}</div>
                    <div className="text-sm text-[#333333aa] leading-snug line-clamp-2 min-h-[2.7em]">
                      {step.desc}
                    </div>
                  </motion.div>
                ))}
              </div>
            </>
          )}
        </div>
      </section>
      <section className="bg-[#F7F4EA] py-20 px-6 md:px-12 lg:px-20">
        {/* Title */}
        <h2 className="text-4xl md:text-5xl font-light text-[#333] mb-12">
          Featured <span className="italic">Projects</span>
        </h2>

        {/* Scrollable Project Cards */}
        <div className="flex overflow-x-auto gap-10 w-full pb-4 overflow-y-hidden">
          {projects.map((project, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 1 }}
              viewport={{ once: true }}
              className="min-w-[400px] max-w-[420px] flex-shrink-0 group cursor-pointer"
            >
              <Link to={`/featured-work/${project.link}`}>
                <div className="relative w-full h-[320px] overflow-hidden rounded-xl mb-4">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover rounded-xl transform transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute top-3 left-3 bg-[#5a5a5a]/80 text-white text-sm px-3 py-1 rounded-full font-light">
                    {project.status}
                  </div>
                </div>
                <h3 className="text-lg font-medium text-[#333]">
                  {project.title}
                </h3>
                <div className="flex justify-center mt-2 text-[#444] text-sm items-center gap-6">
                  <div className="flex items-center gap-1">
                    <BedDouble size={16} /> {project.beds}
                  </div>
                  <div className="flex items-center gap-1">
                    <ShowerHead size={16} /> {project.baths}
                  </div>
                  <div className="flex items-center gap-1">
                    <CarFront size={16} /> {project.cars}
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>
      <section className="flex flex-col md:flex-row items-center justify-center gap-12 bg-[#F7F4EA] py-20 px-6 md:px-20">
        <img
          src="https://arkbuild-cdn.s3.ap-southeast-2.amazonaws.com/images/1Madonna/bathroom.JPG"
          alt="Bathroom Design"
          className="w-full md:w-1/2 rounded-lg object-cover shadow-md"
        />
        <div className="md:w-1/2 text-left space-y-6">
          <p className="text-sm tracking-widest text-[#888]">WHAT WE DO</p>
          <h2 className="text-5xl font-light text-[#333] leading-tight">
            We Can’t <span className="italic font-normal">Wait</span> To Bring
            Your <span className="italic font-normal">Vision</span> To Life
          </h2>
          <p className="text-[#555] text-base leading-relaxed">
            While the custom home industry can feel like navigating a maze,
            we’re here to bridge the gaps and bring your vision to life—that’s
            our promise to you. It takes a team to deliver a dream home, and our
            team is with you right from the start.
          </p>

          <Link to="/featured-work">
            <button className="border border-[#333] text-[#333] px-6 py-3 mt-4 rounded hover:bg-[#333] hover:text-white transition">
              View Our Projects
            </button>
          </Link>
        </div>
      </section>
      <section className="flex flex-col md:flex-row-reverse items-center justify-center gap-12 bg-[#F7F4EA] py-20 px-6 md:px-20">
        <img
          src="https://arkbuild-cdn.s3.ap-southeast-2.amazonaws.com/images/1Madonna/interior.JPG"
          alt="Interior Design"
          className="w-full md:w-1/2 rounded-lg object-cover shadow-md"
        />
        <div className="md:w-1/2 text-left space-y-6">
          <h2 className="text-5xl font-light text-[#333] leading-tight">
            Our <span className="italic font-normal">Difference</span>
          </h2>
          <p className="text-[#555] text-base leading-relaxed">
            We’re passionate about making thoughtful, beautiful designs
            available to more families. By managing everything in-house—from
            design and pricing to approvals and construction— we’re all about
            making your dream home a reality, taking care of the tedious parts
            so you can enjoy the journey.
          </p>
          <Link to="/featured-work">
            <button className="border border-[#333] text-[#333] px-6 py-3 mt-4 rounded hover:bg-[#333] hover:text-white transition">
              Be Inspired
            </button>
          </Link>
        </div>
      </section>
      <Footer />
    </div>
  );
}

export default Homepage;
